import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, ArrowLeft, Upload, FileText, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const WorkerVerification = () => {
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const [verificationRequest, setVerificationRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState(['']);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (user?.role !== 'worker') {
      navigate('/');
      return;
    }
    fetchVerificationRequest();
  }, [user, navigate]);

  const fetchVerificationRequest = async () => {
    try {
      const res = await axios.get(`${API}/verification/my-request`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVerificationRequest(res.data);
    } catch (error) {
      console.error('Error fetching verification request:', error);
    } finally {
      setLoading(false);
    }
  };

  const addDocumentField = () => {
    setDocuments([...documents, '']);
  };

  const updateDocument = (index, value) => {
    const newDocs = [...documents];
    newDocs[index] = value;
    setDocuments(newDocs);
  };

  const removeDocument = (index) => {
    if (documents.length > 1) {
      const newDocs = documents.filter((_, i) => i !== index);
      setDocuments(newDocs);
    }
  };

  const submitVerification = async () => {
    const validDocs = documents.filter(doc => doc.trim() !== '');
    if (validDocs.length === 0) {
      toast.error('Please provide at least one document');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/verification/submit`,
        { documents: validDocs, notes: notes.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Verification request submitted successfully!');
      fetchVerificationRequest();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit verification request');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-[#FFB81C]" />;
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-[#009B4D]" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-[#FFB81C]/20 text-[#FFB81C]';
      case 'approved':
        return 'bg-[#009B4D]/20 text-[#009B4D]';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#003B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#003B5C] font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              data-testid="back-to-dashboard-btn"
              onClick={() => navigate('/dashboard/worker')}
              className="flex items-center space-x-2 text-[#003B5C] hover:text-[#002A42] transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <Wrench className="w-8 h-8" />
              <span className="text-2xl font-bold font-['Outfit']">SkillYaad</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#003B5C] mb-2">Worker Verification</h1>
          <p className="text-[#475569]">Get verified to build trust with customers and increase your bookings</p>
        </div>

        {verificationRequest ? (
          /* Existing Verification Request */
          <Card data-testid="verification-status-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                {getStatusIcon(verificationRequest.status)}
                Verification Status
                <Badge className={getStatusColor(verificationRequest.status)}>
                  {verificationRequest.status.toUpperCase()}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#0F172A] block mb-1">Submitted</label>
                <p className="text-[#475569]">
                  {new Date(verificationRequest.submitted_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>

              {verificationRequest.documents && verificationRequest.documents.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-[#0F172A] block mb-2">Documents Submitted</label>
                  <div className="space-y-2">
                    {verificationRequest.documents.map((doc, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <FileText className="w-4 h-4 text-[#003B5C]" />
                        <span className="text-sm text-[#475569] truncate">{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {verificationRequest.notes && (
                <div>
                  <label className="text-sm font-medium text-[#0F172A] block mb-1">Your Notes</label>
                  <p className="text-[#475569] bg-gray-50 p-3 rounded">{verificationRequest.notes}</p>
                </div>
              )}

              {verificationRequest.status === 'approved' && (
                <div className="bg-[#009B4D]/10 border border-[#009B4D]/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-[#009B4D]" />
                    <span className="font-semibold text-[#009B4D]">Verification Approved!</span>
                  </div>
                  <p className="text-[#475569]">
                    Congratulations! Your worker profile is now verified. This will be displayed to customers and help build trust.
                  </p>
                  {verificationRequest.reviewed_at && (
                    <p className="text-sm text-[#94A3B8] mt-2">
                      Approved on {new Date(verificationRequest.reviewed_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {verificationRequest.status === 'rejected' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <span className="font-semibold text-red-700">Verification Rejected</span>
                  </div>
                  <p className="text-[#475569] mb-3">
                    Your verification request was not approved. Please review the feedback and submit a new request with the required documents.
                  </p>
                  {verificationRequest.notes && (
                    <div className="bg-white p-3 rounded border">
                      <label className="text-sm font-medium text-red-700 block mb-1">Admin Feedback</label>
                      <p className="text-[#475569]">{verificationRequest.notes}</p>
                    </div>
                  )}
                  <Button
                    data-testid="submit-new-verification-btn"
                    onClick={() => setVerificationRequest(null)}
                    className="mt-3 bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full font-bold"
                  >
                    Submit New Request
                  </Button>
                </div>
              )}

              {verificationRequest.status === 'pending' && (
                <div className="bg-[#FFB81C]/10 border border-[#FFB81C]/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-[#FFB81C]" />
                    <span className="font-semibold text-[#FFB81C]">Under Review</span>
                  </div>
                  <p className="text-[#475569]">
                    Your verification request is being reviewed by our team. We'll notify you once the review is complete.
                    This usually takes 1-3 business days.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          /* New Verification Request Form */
          <Card data-testid="verification-form-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-3">
                <Upload className="w-6 h-6 text-[#003B5C]" />
                Submit Verification Request
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Required Documents</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Valid government-issued ID (Driver's License, Passport, or National ID)</li>
                  <li>• Professional certification or license (if applicable)</li>
                  <li>• Proof of insurance (liability insurance recommended)</li>
                  <li>• Business registration (if operating as a business)</li>
                  <li>• References from previous clients (optional but recommended)</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-3">
                  Document Links or Descriptions
                </label>
                {documents.map((doc, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      data-testid={`document-input-${index}`}
                      type="text"
                      value={doc}
                      onChange={(e) => updateDocument(index, e.target.value)}
                      placeholder="Enter document URL or description"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:border-[#003B5C] focus:ring-2 focus:ring-[#003B5C]/20"
                    />
                    {documents.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeDocument(index)}
                        variant="outline"
                        className="px-3 py-2 border-red-200 text-red-600 hover:bg-red-50"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  data-testid="add-document-btn"
                  type="button"
                  onClick={addDocumentField}
                  variant="outline"
                  className="mt-2 border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white"
                >
                  Add Another Document
                </Button>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">
                  Additional Notes (Optional)
                </label>
                <Textarea
                  data-testid="verification-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information you'd like to provide..."
                  className="min-h-[100px]"
                />
              </div>

              <Button
                data-testid="submit-verification-btn"
                onClick={submitVerification}
                disabled={submitting}
                className="w-full bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full py-3 font-bold text-lg"
              >
                {submitting ? 'Submitting...' : 'Submit Verification Request'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Benefits Section */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-[#003B5C]">Benefits of Verification</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-[#009B4D]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-[#009B4D]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">Build Trust</h3>
                <p className="text-sm text-[#475569]">Verified badge shows customers you're legitimate and professional</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#FFB81C]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Wrench className="w-6 h-6 text-[#FFB81C]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">More Bookings</h3>
                <p className="text-sm text-[#475569]">Verified workers get priority in search results and customer preferences</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-[#003B5C]/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FileText className="w-6 h-6 text-[#003B5C]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2">Professional Status</h3>
                <p className="text-sm text-[#475569]">Stand out from unverified workers and command higher rates</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WorkerVerification;